import React, { useCallback, useEffect, useState } from 'react';
import {
    Alert,
    Box,
    Button,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Grid2,
    MenuItem,
    Select,
    TextField,
    Typography,
    Checkbox,
    FormControlLabel,
    FormGroup,
    Tooltip,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTranslation } from 'react-i18next';
import { useGetShows } from '@openapi/shows/shows';
import { useGetVenue } from '@openapi/venues/venues';
import {
    useGetUserCommissionGroups,
    useGetCommissionGroups,
    assignUserCommissionGroup,
    removeUserCommissionGroup,
    getGetUserCommissionGroupsQueryKey,
} from '@openapi/commission-groups/commission-groups';
import useNotify from '../../../hooks/useNotify';
import { useQueryClient } from '@tanstack/react-query';
import AffiliateBookingSnippets from '../../commissiongroups/components/AffiliateBookingSnippets';

function AffiliateCodeDialog({ open, onClose, onSave, initialCode, show, venueSlug }) {
    const { t } = useTranslation();
    const [codeDraft, setCodeDraft] = useState(initialCode);

    useEffect(() => {
        if (open) setCodeDraft(initialCode);
    }, [open, initialCode]);

    const trimmed = codeDraft.trim();
    const isValid = trimmed === '' || /^[a-zA-Z0-9_-]{1,64}$/.test(trimmed);
    const persistedTrimmed = (initialCode || '').trim();
    const isDirty = trimmed !== persistedTrimmed;

    // Previews follow the DRAFT so snippets update live as the admin types.
    // They only "activate" on save — surface an unsaved-changes hint below.
    const previewCode = trimmed && isValid && venueSlug ? trimmed : '';

    return (
        <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
            <DialogTitle>
                {t('commissionGroups.assignmentAffiliateCode.dialogTitle')}
                {' — '}
                {show.title}
            </DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {t('commissionGroups.assignmentAffiliateCode.help')}
                </Typography>
                <TextField
                    fullWidth
                    autoFocus
                    label={t('commissionGroups.assignmentAffiliateCode.label')}
                    placeholder={t('commissionGroups.assignmentAffiliateCode.placeholder')}
                    value={codeDraft}
                    onChange={(e) => setCodeDraft(e.target.value)}
                    error={!isValid}
                    helperText={
                        !isValid ? t('commissionGroups.assignmentAffiliateCode.invalid') : ''
                    }
                />
                {previewCode && (
                    <Box sx={{ mt: 2 }}>
                        <AffiliateBookingSnippets
                            venueSlug={venueSlug}
                            showId={show.id}
                            affiliateCode={previewCode}
                        />
                        {isDirty && (
                            <Typography
                                variant="caption"
                                color="text.secondary"
                                sx={{ mt: 1, display: 'block' }}
                            >
                                {t('commissionGroups.assignmentAffiliateCode.unsavedHint')}
                            </Typography>
                        )}
                    </Box>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>{t('common.cancel')}</Button>
                <Button
                    variant="contained"
                    disabled={!isValid || !isDirty}
                    onClick={() => onSave(trimmed || null)}
                >
                    {t('common.save')}
                </Button>
            </DialogActions>
        </Dialog>
    );
}

const ShowCommissionGroupSelect = ({ show, venueSlug, assignments, onAssign, onRemove }) => {
    const { t } = useTranslation();
    const [dialogOpen, setDialogOpen] = useState(false);

    const { data: groupsData, isLoading: groupsLoading } = useGetCommissionGroups(Number(show.id), {
        query: {
            enabled: !!show.id,
        },
    });

    const currentAssignment = assignments?.find((a) => a.commissionGroup?.showId === show.id);
    const currentValue = currentAssignment?.commissionGroupId ?? '';
    const isManager = currentAssignment?.isManager ?? false;
    const canEdit = currentAssignment?.canEdit ?? false;
    const persistedCode = currentAssignment?.affiliateCode ?? '';

    const handleChange = (e) => {
        const newValue = e.target.value;
        if (newValue === '') {
            if (currentAssignment) {
                onRemove(currentAssignment.id);
            }
        } else {
            // New group selection starts fresh as a manager-less assignment but
            // carries over any affiliate code the user already had on this show.
            onAssign(Number(newValue), {
                isManager: false,
                canEdit: false,
                affiliateCode: persistedCode || null,
            });
        }
    };

    // Each control re-sends the OTHER fields' current values because the backend
    // replaces the whole assignment row — omitting one would reset it.
    const handleManagerToggle = (e) => {
        if (currentAssignment) {
            onAssign(currentAssignment.commissionGroupId, {
                isManager: e.target.checked,
                canEdit,
                affiliateCode: persistedCode || null,
            });
        }
    };

    const handleCanEditToggle = (e) => {
        if (currentAssignment) {
            onAssign(currentAssignment.commissionGroupId, {
                isManager,
                canEdit: e.target.checked,
                affiliateCode: persistedCode || null,
            });
        }
    };

    const handleSaveCode = (nextCode) => {
        if (!currentAssignment) return;
        onAssign(currentAssignment.commissionGroupId, {
            isManager,
            canEdit,
            affiliateCode: nextCode,
        });
        setDialogOpen(false);
    };

    return (
        <Grid2 container spacing={2} alignItems="center" sx={{ mb: 1, textAlign: 'left' }}>
            <Grid2 size={{ xs: 12, md: 3 }}>
                <Typography variant="body2">{show.title}</Typography>
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
                {groupsLoading ? (
                    <CircularProgress size={20} />
                ) : (
                    <Select
                        fullWidth
                        size="small"
                        value={currentValue}
                        onChange={handleChange}
                        displayEmpty
                    >
                        <MenuItem value="">
                            <em>{t('commissionGroups.noAssignment')}</em>
                        </MenuItem>
                        {groupsData?.commissionGroups?.map((group) => (
                            <MenuItem key={group.id} value={group.id}>
                                {group.name}
                            </MenuItem>
                        ))}
                    </Select>
                )}
            </Grid2>
            <Grid2 size={{ xs: 12, md: 3 }}>
                {currentAssignment && (
                    <Button
                        size="small"
                        variant="outlined"
                        startIcon={<EditIcon fontSize="small" />}
                        onClick={() => setDialogOpen(true)}
                        sx={{
                            textTransform: 'none',
                            justifyContent: 'flex-start',
                            width: '100%',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                        }}
                    >
                        {persistedCode
                            ? persistedCode
                            : t('commissionGroups.assignmentAffiliateCode.setCode')}
                    </Button>
                )}
            </Grid2>
            {currentValue && currentAssignment && (
                <Grid2 size={{ xs: 12, md: 3 }}>
                    <FormGroup sx={{ alignItems: 'flex-start' }}>
                        <FormControlLabel
                            sx={{ m: 0, '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' } }}
                            control={
                                <Checkbox
                                    checked={isManager}
                                    onChange={handleManagerToggle}
                                    color="primary"
                                />
                            }
                            label={t('commissionGroups.manager')}
                        />
                        <Tooltip title={t('commissionGroups.canEditBookingsHint')}>
                            <FormControlLabel
                                sx={{
                                    m: 0,
                                    '& .MuiFormControlLabel-label': { whiteSpace: 'nowrap' },
                                }}
                                control={
                                    <Checkbox
                                        checked={canEdit}
                                        onChange={handleCanEditToggle}
                                        color="primary"
                                        disabled={!isManager}
                                    />
                                }
                                label={t('commissionGroups.canEditBookings')}
                            />
                        </Tooltip>
                    </FormGroup>
                </Grid2>
            )}
            {currentAssignment && (
                <AffiliateCodeDialog
                    open={dialogOpen}
                    onClose={() => setDialogOpen(false)}
                    onSave={handleSaveCode}
                    initialCode={persistedCode}
                    show={show}
                    venueSlug={venueSlug}
                />
            )}
        </Grid2>
    );
};

const UserCommissionGroupAssignments = ({ userId, venueId }) => {
    const { t } = useTranslation();
    const { notifyError, notifySuccess } = useNotify();
    const queryClient = useQueryClient();

    const { data: venueData } = useGetVenue(Number(venueId), {
        query: { enabled: !!venueId },
    });
    const venueSlug = venueData?.slug ?? '';

    const { data: showsData, isLoading: showsLoading } = useGetShows(
        {
            venueId: Number(venueId),
            page: 0,
            pageSize: 1000,
        },
        {
            query: {
                enabled: !!venueId,
            },
        },
    );

    const { data: assignmentsData, isLoading: assignmentsLoading } = useGetUserCommissionGroups(
        Number(venueId),
        Number(userId),
        {
            query: {
                enabled: !!venueId && !!userId,
            },
        },
    );

    const invalidateAssignments = useCallback(() => {
        queryClient.invalidateQueries({
            queryKey: getGetUserCommissionGroupsQueryKey(Number(venueId), Number(userId)),
        });
    }, [queryClient, venueId, userId]);

    const handleAssign = useCallback(
        async (
            commissionGroupId,
            { isManager = false, canEdit = false, affiliateCode = null } = {},
        ) => {
            try {
                await assignUserCommissionGroup({
                    userId: Number(userId),
                    commissionGroupId,
                    isManager,
                    canEdit,
                    affiliateCode: affiliateCode || null,
                });
                notifySuccess(t('commissionGroups.notifications.assigned'));
                invalidateAssignments();
            } catch (err) {
                notifyError(err);
            }
        },
        [userId, notifySuccess, notifyError, t, invalidateAssignments],
    );

    const handleRemove = useCallback(
        async (assignmentId) => {
            try {
                await removeUserCommissionGroup(assignmentId);
                notifySuccess(t('commissionGroups.notifications.removed'));
                invalidateAssignments();
            } catch (_err) {
                notifyError(t('commissionGroups.notifications.removeError'));
            }
        },
        [notifySuccess, notifyError, t, invalidateAssignments],
    );

    if (showsLoading || assignmentsLoading) {
        return (
            <Box display="flex" justifyContent="center" p={2}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    const shows = showsData?.shows || [];

    if (shows.length === 0) {
        return <Alert severity="info">{t('shows.noShows')}</Alert>;
    }

    return (
        <Box>
            <Alert severity="info" sx={{ mb: 2, textAlign: 'left' }}>
                {t('commissionGroups.assignmentsInfo')}
            </Alert>
            {shows.map((show) => (
                <ShowCommissionGroupSelect
                    key={show.id}
                    show={show}
                    venueSlug={venueSlug}
                    assignments={assignmentsData?.assignments || []}
                    onAssign={handleAssign}
                    onRemove={handleRemove}
                />
            ))}
        </Box>
    );
};

export default UserCommissionGroupAssignments;
